import { createContext, useContext, ReactNode, useRef, useEffect } from 'react'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useAuthStore } from '@/store/authStore'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import { playDetectionSound } from '@/components/notifications/shared'

interface WebSocketContextValue {
  isConnected: boolean
  lastEvent: any
  reconnect: () => void
  disconnect: () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  const { addNotification } = useNotifications()

  // Keep a stable ref so the onMessage callback always calls the latest addNotification
  const addNotifRef = useRef(addNotification)
  useEffect(() => { addNotifRef.current = addNotification }, [addNotification])

  // Track whether the user has interacted with the page so audio can play
  const hasInteractedRef = useRef(false)
  useEffect(() => {
    const mark = () => { hasInteractedRef.current = true }
    window.addEventListener('click', mark, { once: true })
    window.addEventListener('keydown', mark, { once: true })
    return () => {
      window.removeEventListener('click', mark)
      window.removeEventListener('keydown', mark)
    }
  }, [])

  const websocket = useWebSocket({
    enabled: isAuthenticated,
    onMessage: (message) => {
      if (message.type === 'notification') {
        console.log('[WebSocket] Event notification received:', message.event)
        addNotifRef.current(message.event)
        if (hasInteractedRef.current) {
          playDetectionSound()
        }
      }
    },
    onError: (error) => {
      console.error('[WebSocket] Connection error:', error)
    },
  })

  return (
    <WebSocketContext.Provider value={websocket}>
      {children}
    </WebSocketContext.Provider>
  )
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider')
  }
  return context
}
